import React, {Fragment, useCallback, useEffect, useMemo, useState} from 'react';
import {connect} from 'react-redux';
import {translate} from 'react-i18next';
import {compose} from 'redux';
import EnhancedModal from "../../../components/enhanced-modal";
import FirstStep from "./page-components/FirstStep";
import SecondStep from "./page-components/SecondStep";
import {Button, Form, Steps} from "antd";
import TransformLoader from "./page-components/TransformLoader";
import {withRouter} from "react-router-dom";
import {pluginFormChange, pluginFormErase, submitPluginConvertAction} from "./actions";
import {DataLanguageConsumer} from "../../../contexts/DataLanguage";
import {PATH_PLUGINS} from "../../../constants/paths";
import {GUTTER_MD} from "../../../styles/constants";
import "./style.css";

const mapStateToProps = state => {
    const dynamicSidebarChildren = (state.app.dynamicSidebarChildren || []).find(item => item.childPath === PATH_PLUGINS);
    return {
        appLanguage: state.app.language,
        plugins: dynamicSidebarChildren ? dynamicSidebarChildren.data : [],
        pluginForm: state.scenes.utilities.plugins.pluginForm,
        username: state.app.user.username,
        fileServerPath: state.scenes.utilities.plugins.fileServerPath,
        secondServerPath: state.scenes.utilities.plugins.secondServerPath,
        thirdServerPath: state.scenes.utilities.plugins.thirdServerPath,
        outputFilePath: state.scenes.utilities.plugins.outputFilePath,
        convertError: state.scenes.utilities.plugins.convertError,
        permissions: state.app.user.permissions,
    }
}

const mapDispatchToProps = dispatch => ({
    onSubmit: (pluginUid, fileServerPath, transformFileServerPath, processFileServerPath, lang, outputExtension, dllName) =>
        dispatch(submitPluginConvertAction(pluginUid, fileServerPath, transformFileServerPath, processFileServerPath, lang, outputExtension, dllName)),

    onChange: fields => dispatch(pluginFormChange(fields)),
    eraseForm: () => dispatch(pluginFormErase())
});

const getPluginUidFromURLParam = (param) => param.split("uid=")[1].split("&")[0]

const mapPropsToFields = ({pluginForm}) => {
    return {
        file: Form.createFormField({value: pluginForm.file || null}),
        transformFileInput: Form.createFormField({value: pluginForm.transformFileInput || null}),
        processFileInput: Form.createFormField({value: pluginForm.processFileInput || null}),
    }
};

const onFieldsChange = (props, fields) => props.onChange(fields);

const index = ({
                   form, t, plugins, location, fileServerPath,
                   secondServerPath, thirdServerPath, username,
                   onSubmit, appLanguage, outputFilePath,
                   history, pluginForm, permissions, eraseForm, convertError
               }) => {
    const hasPermissionForPlugins = useMemo(() => !!permissions && permissions.functionality.includes("plugins"), [permissions]);
    useEffect(() => {
        if (!hasPermissionForPlugins) {
            history.push("/");
        }
    }, [hasPermissionForPlugins]);

    const plugin = useMemo(() => {
        if (!location || !location.search || !plugins || plugins.length === 0) {
            return null;
        }
        const pluginUid = getPluginUidFromURLParam(location.search);
        return plugins.find(item => item.PluginUid === pluginUid)
    }, [location, plugins]);

    const [visible, setVisible] = useState(false);
    const [step, setStep] = useState(1);
    const [isTransforming, setIsTransforming] = useState(false);

    const transformFileServerPath = useMemo(() => {
        if (!secondServerPath || !plugin.TransformFileEnabled) {
            return null;
        }
        return secondServerPath;
    }, [secondServerPath, plugin]);

    const processFileServerPath = useMemo(() => {
        if (!secondServerPath || !plugin.ProcessFileEnabled) {
            return null;
        }
        if (secondServerPath && !plugin.TransformFileEnabled) {
            return secondServerPath;
        }
        return thirdServerPath;
    }, [secondServerPath, thirdServerPath, plugin]);

    useEffect(() => {
        if (plugin) {
            setTimeout(() => setVisible(true), 0);
        }
    }, [plugin]);

    const changeStep = useCallback((lang) => {
        try {
            setIsTransforming(true);
            onSubmit(plugin.PluginUid, fileServerPath, transformFileServerPath, processFileServerPath, lang, plugin.OutputExtension, plugin.DllName);
        } catch (e) {
            setIsTransforming(false);
        }
    }, [setIsTransforming, setStep, plugin, fileServerPath, transformFileServerPath, processFileServerPath]);

    useEffect(() => {
        if (isTransforming && !!outputFilePath) {
            setIsTransforming(false);
            setStep(2);
        }
    }, [outputFilePath, isTransforming]);

    useEffect(() => {
        if (isTransforming && convertError) {
            setIsTransforming(false);
        }
    }, [convertError, isTransforming]);

    if (!username) {
        return null
    }

    if (!plugins || plugins.length === 0 || !plugin) {
        return <TransformLoader/>
    }

    return (
        <DataLanguageConsumer>
            {dataLanguage => {
                const lang = dataLanguage || appLanguage;
                return (
                    <EnhancedModal visible={visible}
                                   title={<strong>{plugin.label[lang]}</strong>}
                                   onCancel={() => {
                                       setVisible(false);
                                       setStep(1);
                                       history.push("/");
                                   }} footer={isTransforming ? <Button onClick={() => {
                        setIsTransforming(false);
                        setStep(1);
                    }}>{t('commons.buttons.goBack.title')}</Button> : step === 1 ?
                        <Button type="primary"
                                disabled={!fileServerPath ||
                                    (plugin.TransformFileEnabled && !transformFileServerPath) ||
                                    (plugin.ProcessFileEnabled && !processFileServerPath)}
                                onClick={() => changeStep(lang)}>{t('commons.buttons.transform.title')}</Button> : null}>
                        <Fragment>
                            <Steps current={step} size="small" style={{marginBottom: GUTTER_MD}} className="plugin__wizard">
                                <Steps.Step
                                    title={
                                        <span>
                                        {t('scenes.utilities.plugin.modal.input.title')}
                                    </span>
                                    }
                                    icon={
                                        <Button
                                            shape="circle"
                                            type={step === 1 ? 'primary' : null}
                                            size="small"
                                        >
                                            1
                                        </Button>
                                    }
                                    description={
                                        pluginForm.file
                                            ? <span title={pluginForm.file.name}>{pluginForm.file.name}</span>
                                            : null
                                    }
                                    onClick={() => {
                                        if (step !== 2) return;
                                        eraseForm();
                                        form.resetFields();
                                        setStep(1);
                                    }}
                                />
                                <Steps.Step
                                    title={
                                        <span>
                                        {t('scenes.utilities.plugin.modal.output.title')}
                                    </span>
                                    }
                                    icon={
                                        <Button
                                            shape="circle"
                                            type={step === 2 ? 'primary' : null}
                                            size="small"
                                        >
                                            2
                                        </Button>
                                    }
                                />
                            </Steps>
                            {
                                isTransforming ? <TransformLoader/> :
                                    step === 1 ?
                                        <FirstStep plugin={plugin} form={form}/> :
                                        <SecondStep plugin={plugin} lang={lang}/>
                            }
                        </Fragment>
                    </EnhancedModal>
                )
            }}
        </DataLanguageConsumer>
    );
};

export default compose(
    translate(),
    connect(mapStateToProps, mapDispatchToProps),
    Form.create({mapPropsToFields, onFieldsChange})
)(withRouter(index));